/*!
 * Copyright © 2018. Verizon Connect Ireland Limited. All rights reserved.
 */

import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog, MAT_LEGACY_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { ICoordinates, INavigationEnabledVehicles, IReverseGeocode } from '../../../core/models';
import { GetSendToGarminModalData, SendNavigationStop } from '../../../core/store/actions';
import { getGeocodeInfo, getNavigationEnabledVehicles } from '../../../core/store/selectors';

@Component({
  selector: 'fm-send-to-garmin',
  templateUrl: './send-to-garmin.component.html',
  styleUrls: ['./send-to-garmin.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SendToGarminComponent implements OnInit, OnDestroy {
  public vehicleSelection$: Observable<INavigationEnabledVehicles[]>;
  public sendToGarminForm: UntypedFormGroup;
  public get description(): AbstractControl {
    return this.sendToGarminForm.get('description');
  }
  public get vehicle(): AbstractControl {
    return this.sendToGarminForm.get('vehicle');
  }

  public selectedVehicle: INavigationEnabledVehicles;

  private _fullAddress: string;
  private _geocodeInfo$: Observable<IReverseGeocode>;
  private readonly _unsubscribe$ = new Subject<void>();

  constructor(
    private readonly _store: Store,
    private readonly _dialog: MatLegacyDialog,
    @Inject(MAT_LEGACY_DIALOG_DATA) private readonly _dialogData: { coordinates: ICoordinates }
  ) {}

  public ngOnInit(): void {
    this._store.dispatch(new GetSendToGarminModalData(this._dialogData.coordinates));

    this.sendToGarminForm = new UntypedFormGroup({
      description: new UntypedFormControl(this._fullAddress),
      vehicle: new UntypedFormControl(null, Validators.required)
    });
    this.vehicleSelection$ = this._store.pipe(select(getNavigationEnabledVehicles));
    this._geocodeInfo$ = this._store.pipe(select(getGeocodeInfo));
    this._geocodeInfo$
      .pipe(
        takeUntil(this._unsubscribe$),
        map(geocodeInfo => geocodeInfo?.fullAddress)
      )
      .subscribe(address => {
        if (this.sendToGarminForm && address) {
          this.description.setValue(address);
        } else if (address) {
          this._fullAddress = address;
        }
      });
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public onSendClick(): void {
    this._geocodeInfo$.pipe(take(1)).subscribe((geocodeInfo: IReverseGeocode) => {
      if (geocodeInfo) {
        const addressInfo = geocodeInfo.address;
        this._store.dispatch(
          new SendNavigationStop({
            garminInfo: {
              description: this.description.value,
              vehicleId: this.vehicle.value
            },
            addressInfo
          })
        );

        this._dialog.closeAll();
      }
    });
  }
}
